/*
 * Keystone Supply Group: Constant Contact form helper.
 *
 * Include once per page, after the site's own scripts:
 *   <script src="https://YOUR-SERVICE.up.railway.app/ksg-subscribe.js"
 *           data-endpoint="https://YOUR-SERVICE.up.railway.app/subscribe"
 *           data-key="SITE_FORM_SECRET value, or omit" defer></script>
 *
 * Any <form data-cc-source="newsletter|email-alerts|blog|quote-request|footer|account"> gets a second submit listener
 * that posts JSON to the endpoint. It never calls preventDefault and never stops propagation, so the site's existing
 * submit handling (form endpoint or mailto fallback) runs exactly as before, including when this service is down.
 *
 * Consent rules:
 *   data-cc-consent="fieldname"  only that checkbox counts
 *   a checkbox named "subscribe" only when checked (quote forms: the visitor asked for the list)
 *   a checkbox named "consent"   only when checked (alert forms)
 *   none of the above            the form exists only to join the list; pressing Subscribe is the opt-in
 *
 * Categories: checked checkboxes named cat_* or categories[] contribute their value.
 */
(function () {
  'use strict';
  if (typeof window === 'undefined' || typeof document === 'undefined' || !window.fetch) return;

  var script = document.currentScript;
  var endpoint = (script && script.getAttribute('data-endpoint')) || (window.KSG && window.KSG.ccEndpoint) || '';
  var formKey = (script && script.getAttribute('data-key')) || (window.KSG && window.KSG.ccFormKey) || '';
  var TIMEOUT_MS = 6000;
  var DEFAULT_OK = 'You are on the Keystone email list. Look for a confirmation from Keystone Supply Group.';
  var DEFAULT_ERR = 'Email list signup did not complete. Your form was still sent. Call (320) 808-3686 if you want to be added by hand.';
  if (!endpoint) return;

  function field(form, name) {
    // Shopify contact forms rename inputs to contact[name]; accept both spellings.
    return form.querySelector('[name="' + name + '"]') || form.querySelector('[name="contact[' + name + ']"]');
  }
  function text(form, name) {
    var f = field(form, name);
    return f && f.value != null ? String(f.value).trim() : '';
  }
  function consentGiven(form) {
    var explicit = form.getAttribute('data-cc-consent');
    if (explicit) {
      var c = field(form, explicit);
      return !!(c && c.checked);
    }
    var sub = field(form, 'subscribe');
    if (sub && sub.type === 'checkbox') return !!sub.checked;
    var con = field(form, 'consent');
    if (con && con.type === 'checkbox') return !!con.checked;
    return true;
  }
  function categories(form) {
    var out = [];
    var boxes = form.querySelectorAll('input[type="checkbox"]:checked');
    for (var i = 0; i < boxes.length; i++) {
      var n = boxes[i].name || '';
      if (n.indexOf('contact[') === 0) n = n.slice(8, -1);
      if (n.indexOf('cat_') !== 0 && n !== 'categories' && n !== 'categories[]') continue;
      var v = String(boxes[i].value || '').trim();
      if (v && out.indexOf(v) === -1) out.push(v);
    }
    return out;
  }
  function statusEl(form) {
    var el = form.__ksgCcStatus;
    if (!el || !el.parentNode) {
      el = document.createElement('p');
      el.className = 'ksg-cc-status small';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      el.hidden = true;
      if (form.parentNode) form.parentNode.insertBefore(el, form.nextSibling);
      form.__ksgCcStatus = el;
    }
    return el;
  }
  function show(form, message, state) {
    var el = statusEl(form);
    el.textContent = message || '';
    el.hidden = !message;
    el.setAttribute('data-state', state || '');
  }
  function payload(form) {
    var src = form.getAttribute('data-cc-source') || text(form, 'source');
    var body = {
      email: text(form, 'email'),
      first_name: text(form, 'first_name'),
      last_name: text(form, 'last_name'),
      name: text(form, 'name'),
      organization: text(form, 'organization') || text(form, 'company') || text(form, 'facility'),
      source: src,
      page_url: String(window.location.href).split('#')[0],
      categories: categories(form),
      consent: true,
      company_website: text(form, 'company_website') || text(form, 'honeypot'),
    };
    return body;
  }
  function send(form) {
    if (form.__ksgCcSent) return;
    var hp = form.querySelector('.hp, [name="company_website"], [name="honeypot"]');
    if (hp && hp.value) return;
    if (typeof form.checkValidity === 'function' && !form.checkValidity()) return;
    if (!consentGiven(form)) return;
    var body = payload(form);
    if (!body.email || !body.source) return;

    var controller = window.AbortController ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, TIMEOUT_MS) : null;
    var headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
    if (formKey) headers['X-KSG-Form-Key'] = formKey;
    show(form, '', '');

    return fetch(endpoint, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(body),
      keepalive: true,
      signal: controller ? controller.signal : undefined
    })
      .then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (data) { return { status: r.status, data: data || {} }; });
      })
      .then(function (out) {
        if (out.status >= 200 && out.status < 300 && out.data.ok) {
          form.__ksgCcSent = true;
          if (out.data.configured === false) return; // credentials not set yet; the site's own confirmation covers the visitor
          show(form, form.getAttribute('data-cc-success') || DEFAULT_OK, 'ok');
        } else if (out.status === 429) {
          show(form, 'Too many signups from this connection. Try again in a few minutes.', 'error');
        } else {
          show(form, form.getAttribute('data-cc-error') || DEFAULT_ERR, 'error');
        }
      })
      .catch(function () {
        show(form, form.getAttribute('data-cc-error') || DEFAULT_ERR, 'error');
      })
      .then(function () {
        if (timer) clearTimeout(timer);
      });
  }
  function attach(form) {
    if (!form || form.__ksgCcAttached) return;
    form.__ksgCcAttached = true;
    form.addEventListener('submit', function () {
      try {
        send(form);
      } catch (e) {
        /* never interfere with the form */
      }
    }, false);
  }
  function init() {
    var forms = document.querySelectorAll('form[data-cc-source]');
    for (var i = 0; i < forms.length; i++) attach(forms[i]);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  window.KSGSubscribe = { send: send, attach: attach, init: init, endpoint: endpoint };
})();
